import { PictogramKey } from "./dotPictograms";

/**
 * How a design's diamond is filled. DOT labels are not all one colour:
 * class 8 is white over black, class 7 is yellow over white, class 4.1 is
 * red-and-white vertical stripes. A solid stand-in for any of these is a
 * WRONG DOT marking on a document a customer may treat as compliant, so the
 * shape carries the real background instead of collapsing to one colour.
 */
export type HazardLabelBackground =
  | { kind: "SOLID"; color: string }
  /** Upper half over lower half, split on the diamond's horizontal diagonal. */
  | { kind: "SPLIT"; topColor: string; bottomColor: string }
  /** Vertical stripes -- HAZARD_LABEL_STRIPE_COUNT stripes of `stripeColor` on
   * `groundColor`. */
  | { kind: "STRIPED"; stripeColor: string; groundColor: string };

/**
 * The DOT design for one hazard class: its background, the colour the class
 * number is set in, and the symbol from `dotPictograms.ts` that goes in the
 * diamond's upper half.
 *
 * `pictogram` is absent only on the two division-unknown entries ("2", "6"),
 * where the division decides the symbol and there is no division to read.
 */
export interface HazardLabelDesign {
  background: HazardLabelBackground;
  /** The class number's colour. Follows the design convention -- dark text
   * on light labels, white on saturated ones -- and on a split design the
   * colour readable against the LOWER half, where the number sits. */
  textColor: string;
  pictogram?: PictogramKey;
  /** The symbol's colour where it differs from `textColor`. On a split
   * design the symbol is in the upper half and the number in the lower, so
   * the two need different colours. Defaults to `textColor`. */
  pictogramColor?: string;
  /**
   * Draw the symbol across the diamond's whole upper half instead of in the
   * small centred symbol slot.
   *
   * Class 9 only. Its "symbol" is the seven vertical bars, and on a real
   * design they ARE the upper half, edge to edge -- not a device sitting in
   * the middle of it. Rendered in the ordinary slot they came out as a small
   * graphic that read as decoration rather than as the class 9 marking.
   */
  pictogramSpansUpperHalf?: boolean;

  /** Class 9 only: its class number is underlined. No other class is. */
  underlineNumber?: boolean;
}

export type HazardClassDiamondEntry = {
  name: string;
  design: HazardLabelDesign;
};

/**
 * The design's geometry, as ratios of the diamond's own rendered size.
 *
 * Shared because both renderers draw the same design and previously drifted
 * whenever one of them was tuned alone -- the preview's job is to show what
 * will print. pdf-generator multiplies these by PDF points, Oak by CSS
 * pixels.
 */
export const HAZARD_LABEL_CORNER_RADIUS_RATIO = 0.12;
export const HAZARD_LABEL_SYMBOL_SIZE_RATIO = 0.3;
/** The symbol's centre, as a fraction of the diamond's height from its top.
 * Puts it in the upper half, clear of both the top point and the number. */
export const HAZARD_LABEL_SYMBOL_CENTER_RATIO = 0.32;
/** The class number's top edge, as a fraction of the diamond's height. */
export const HAZARD_LABEL_NUMBER_TOP_RATIO = 0.62;
export const HAZARD_LABEL_STRIPE_COUNT = 7;
/** How much wider than tall the centred symbol slot is. The symbols are not
 * square -- the corrosion symbol is over 2:1, the gas cylinder over 3:1 -- and
 * each is fitted inside this box preserving its own aspect. */
export const HAZARD_LABEL_SYMBOL_WIDTH_RATIO = 1.6;

/** For `pictogramSpansUpperHalf`: the symbol's width as a fraction of the
 * diamond's, and the height of its centre. Sized to fill the upper half
 * without crossing the diamond's left and right points. */
export const HAZARD_LABEL_UPPER_HALF_WIDTH_RATIO = 0.66;
export const HAZARD_LABEL_UPPER_HALF_CENTER_RATIO = 0.34;

/** A white design on a white label is invisible without an outline. The
 * coloured labels carry none, so this applies to the white fill only. */
export const HAZARD_LABEL_WHITE_OUTLINE_RATIO = 0.02;

/** The Class 9 label underlines its class number; no other class does. The
 * rule is the width and thickness as fractions of the NUMBER's own font size,
 * so the underline tracks the digit at any label size. */
export const HAZARD_LABEL_UNDERLINE_WIDTH_RATIO = 0.62;
export const HAZARD_LABEL_UNDERLINE_THICKNESS_RATIO = 0.08;
export const HAZARD_LABEL_UNDERLINE_GAP_RATIO = 0.06;

/**
 * 49 CFR 172.407(c) requires a solid line inner border approximately 5mm
 * inside each edge and parallel to it, on a label at least 100mm per side.
 *
 * Expressed against the diamond's BOUNDING SQUARE, which is what `size` is
 * everywhere in the renderers. A square-on-point with a 100mm side spans
 * 100 * sqrt(2) = 141.42mm point to point, so 5mm of perpendicular inset is
 * 5 / 141.42 of that span.
 */
export const HAZARD_LABEL_INNER_BORDER_INSET_RATIO = 5 / (100 * Math.SQRT2);

/** The inner border's line weight. 172.407(c) gives no width, so this is a
 * 1.5mm line at the regulated size, scaled with everything else. */
export const HAZARD_LABEL_INNER_BORDER_WIDTH_RATIO = 1.5 / (100 * Math.SQRT2);

/**
 * How far a square-on-point's bounding square shrinks when every edge moves
 * `inset` inwards perpendicular to itself. The perpendicular distance from
 * the centre to an edge is half the span over sqrt(2), so removing `inset`
 * from it takes `inset * 2 * sqrt(2)` off the span.
 */
export function diamondSpanInsetBy(span: number, inset: number): number {
  return span - inset * 2 * Math.SQRT2;
}

const RED = "#C0392B";
const WHITE = "#FFFFFF";
const BLACK = "#000000";
const ORANGE = "#F97316";
const GREEN = "#16A34A";
const BLUE = "#2563EB";
const YELLOW = "#FACC15";

export const HAZARD_CLASS_DIAMOND: Record<string, HazardClassDiamondEntry> = {
  "1": {
    name: "EXPLOSIVES",
    design: {
      background: { kind: "SOLID", color: ORANGE },
      textColor: BLACK,
      pictogram: "EXPLODING_BOMB",
    },
  },
  // Division unknown: the symbol differs by division (flame, cylinder,
  // skull), so no symbol is claimed. The renderer falls back to the text
  // line and the label surface warns that the division is missing.
  "2": {
    name: "GASES",
    design: { background: { kind: "SOLID", color: WHITE }, textColor: BLACK },
  },
  "2.1": {
    name: "FLAMMABLE GAS",
    design: { background: { kind: "SOLID", color: RED }, textColor: WHITE, pictogram: "FLAME" },
  },
  "2.2": {
    name: "NON-FLAMMABLE GAS",
    design: {
      background: { kind: "SOLID", color: GREEN },
      textColor: WHITE,
      pictogram: "GAS_CYLINDER",
    },
  },
  "2.3": {
    name: "POISON GAS",
    design: { background: { kind: "SOLID", color: WHITE }, textColor: BLACK, pictogram: "SKULL" },
  },
  "3": {
    name: "FLAMMABLE LIQUID",
    design: { background: { kind: "SOLID", color: RED }, textColor: WHITE, pictogram: "FLAME" },
  },
  "4.1": {
    name: "FLAMMABLE SOLID",
    design: {
      background: { kind: "STRIPED", stripeColor: RED, groundColor: WHITE },
      textColor: BLACK,
      pictogram: "FLAME",
      pictogramColor: BLACK,
    },
  },
  "4.2": {
    name: "SPONTANEOUSLY COMBUSTIBLE",
    design: {
      background: { kind: "SPLIT", topColor: WHITE, bottomColor: RED },
      textColor: WHITE,
      pictogram: "FLAME",
      pictogramColor: BLACK,
    },
  },
  "4.3": {
    name: "DANGEROUS WHEN WET",
    design: { background: { kind: "SOLID", color: BLUE }, textColor: WHITE, pictogram: "FLAME" },
  },
  "5.1": {
    name: "OXIDIZER",
    design: {
      background: { kind: "SOLID", color: YELLOW },
      textColor: BLACK,
      pictogram: "FLAME_OVER_CIRCLE",
    },
  },
  "5.2": {
    name: "ORGANIC PEROXIDE",
    design: {
      background: { kind: "SPLIT", topColor: RED, bottomColor: YELLOW },
      textColor: BLACK,
      // A plain flame, not 5.1's flame-over-circle: the oxidizer device
      // belongs to division 5.1 only.
      pictogram: "FLAME",
      pictogramColor: BLACK,
    },
  },
  // Division unknown -- see the note on "2".
  "6": {
    name: "TOXIC OR INFECTIOUS",
    design: { background: { kind: "SOLID", color: WHITE }, textColor: BLACK },
  },
  "6.1": {
    name: "POISON",
    design: { background: { kind: "SOLID", color: WHITE }, textColor: BLACK, pictogram: "SKULL" },
  },
  "6.2": {
    name: "INFECTIOUS SUBSTANCE",
    design: {
      background: { kind: "SOLID", color: WHITE },
      textColor: BLACK,
      // The biohazard symbol. Division 6.1 uses the skull; printing it here
      // states the wrong hazard.
      pictogram: "BIOHAZARD",
      pictogramColor: BLACK,
    },
  },
  "7": {
    name: "RADIOACTIVE",
    design: {
      background: { kind: "SPLIT", topColor: YELLOW, bottomColor: WHITE },
      textColor: BLACK,
      pictogram: "TREFOIL",
      pictogramColor: BLACK,
    },
  },
  "8": {
    name: "CORROSIVE",
    design: {
      background: { kind: "SPLIT", topColor: WHITE, bottomColor: BLACK },
      textColor: WHITE,
      pictogram: "CORROSION",
      pictogramColor: BLACK,
    },
  },
  "9": {
    name: "MISCELLANEOUS",
    design: {
      background: { kind: "SOLID", color: WHITE },
      textColor: BLACK,
      pictogram: "CLASS_9_BARS",
      pictogramColor: BLACK,
      pictogramSpansUpperHalf: true,
      underlineNumber: true,
    },
  },
};

export function diamondForHazardClass(dotHazardClass: string): HazardClassDiamondEntry | undefined {
  const key = dotHazardClass.trim();
  return HAZARD_CLASS_DIAMOND[key] ?? HAZARD_CLASS_DIAMOND[key.split(".")[0]];
}

/**
 * Whether a hazard class names a class but not the division inside it.
 *
 * Class 2 splits into 2.1, 2.2 and 2.3, and class 6 into 6.1 and 6.2. Each
 * division takes a different symbol -- flame, gas cylinder, skull, biohazard
 * -- so "2" alone cannot produce a label, and 2.3 also has to carry
 * the "Inhalation Hazard" marking. Read off the table: a class with an entry but no
 * symbol is one that needs a division.
 */
export function hazardClassNeedsDivision(hazardClass: string | null | undefined): boolean {
  const entry = hazardClass ? HAZARD_CLASS_DIAMOND[hazardClass.trim()] : undefined;
  return entry !== undefined && entry.design.pictogram === undefined;
}

/** The divisions a class splits into, in order, for the message that asks a
 * user to pick one. Empty for a class that needs no division. */
export function divisionsOfHazardClass(hazardClass: string | null | undefined): string[] {
  const trimmed = hazardClass?.trim();
  if (!trimmed || !hazardClassNeedsDivision(trimmed)) {
    return [];
  }
  return Object.keys(HAZARD_CLASS_DIAMOND).filter((key) => key.startsWith(`${trimmed}.`));
}

/** Why this hazard class cannot print a label, or undefined when it can. */
export function hazardClassProblem(hazardClass: string | null | undefined): string | undefined {
  const divisions = divisionsOfHazardClass(hazardClass);
  if (divisions.length === 0) {
    return undefined;
  }
  return `Hazard class ${hazardClass?.trim()} needs a division. Each one takes a different DOT symbol, so pick ${divisions.join(", ")}.`;
}

/**
 * Why this class prints no regulated full-size label, or undefined when it
 * prints one.
 *
 * Class 7 has no generic package label. 49 CFR 172.403(b)-(c) picks between
 * RADIOACTIVE WHITE-I, YELLOW-II and YELLOW-III by the package's transport
 * index and its surface radiation level, and each carries the contents and
 * activity. None of that is on a waste profile, so the trefoil diamond alone
 * would print as a compliant label while missing everything that decides
 * which label it is.
 */
export function regulatedLabelProblem(hazardClass: string | null | undefined): string | undefined {
  if (hazardClass?.trim() !== "7") {
    return undefined;
  }
  return (
    "Hazard class 7 takes a RADIOACTIVE WHITE-I, YELLOW-II or YELLOW-III label, " +
    "chosen by the package's transport index and surface radiation level (49 CFR 172.403). " +
    "The label is not printed here, because a waste profile does not carry those readings."
  );
}

/**
 * Whether this class prints a regulated full-size hazard label of its own.
 * The PDF's page list and the placement instruction both read this, so an
 * instruction cannot point at a page that never prints.
 */
export function printsRegulatedHazardLabel(hazardClass: string | null | undefined): boolean {
  if (!hazardClass || regulatedLabelProblem(hazardClass)) {
    return false;
  }
  return diamondForHazardClass(hazardClass)?.design.pictogram !== undefined;
}
